import { isHistoryNavigation } from 't-lib-ui/router';

// Lets "back to Home" buttons return to a spot on the Home page: either where the user left off
// (e.g. the preview of an unfollowed feed opened from Discover) or a Discover category (its "See
// all" page). In memory only: the position is saved whenever Home unmounts, and restored when
// requested or when Home is reached through the browser history (Back, Android's back button).

export type HomeScrollTarget = { top: number } | { category: string };

let savedScrollTop = 0;
let requested: HomeScrollTarget | undefined;

export function saveHomeScroll(scrollTop: number): void {
  savedScrollTop = scrollTop;
}

/** Ask the next Home page mount to restore the saved scroll position. */
export function requestHomeScrollRestore(): void {
  requested = { top: savedScrollTop };
}

/** Ask the next Home page mount to scroll to a Discover category (by slug). */
export function requestHomeScrollToCategory(slug: string): void {
  requested = { category: slug };
}

/** The scroll target for this mount, if one was requested (consumes the request). */
export function takeHomeScrollTarget(): HomeScrollTarget | undefined {
  const target = requested ?? (isHistoryNavigation() ? { top: savedScrollTop } : undefined);
  requested = undefined;
  return target;
}

/** DOM id of a Discover category's block on the Home page. */
export const homeCategoryAnchor = (slug: string) => `home-category-${slug}`;

// Where the back button of an unfollowed feed's preview leads: Home (restoring its scroll
// position) unless the preview was opened from a Discover category page.
let previewReturnPath: string | undefined;

/** Remember the page a feed preview is opened from (undefined: Home). */
export function setPreviewReturnPath(path: string | undefined): void {
  previewReturnPath = path;
}

export function getPreviewReturnPath(): string | undefined {
  return previewReturnPath;
}

// The Add Feed page's state when it was left: its scroll position and how many pages ("Load More")
// of the category / collection list were loaded. Restored when a feed's back button or the browser
// history (Back, Android's back button) returns to the same Add Feed path.
export type AddFeedReturnState = { path: string; scrollTop: number; chunks: number };
let addFeedReturn: AddFeedReturnState | undefined;
let addFeedRestoreRequested = false;

export function saveAddFeedReturn(state: AddFeedReturnState): void {
  addFeedReturn = state;
}

/** Ask the next Add Feed mount to restore its saved state. */
export function requestAddFeedRestore(): void {
  addFeedRestoreRequested = true;
}

/** The state to restore for this Add Feed mount, if requested for its path (consumes the request). */
export function takeAddFeedRestore(path: string): AddFeedReturnState | undefined {
  const state = (addFeedRestoreRequested || isHistoryNavigation()) && addFeedReturn?.path == path ? addFeedReturn : undefined;
  addFeedRestoreRequested = false;
  return state;
}

// Whether Add Feed's topic pages were entered from Home's Categories section: their header then
// leads back to Home instead of the Add Feed start page. Set by Home's category links; cleared
// when Home mounts and when the Add Feed start page is shown.
let addFeedFromHome = false;

export function setAddFeedFromHome(fromHome: boolean): void {
  addFeedFromHome = fromHome;
}

export function isAddFeedFromHome(): boolean {
  return addFeedFromHome;
}

// Whether the current topic page was opened from the "All topics" page: its header then leads back
// there (one level up) instead of to the Add Feed start page or Home. Set by the topic tiles.
let topicViaAllTopics = false;

export function setTopicViaAllTopics(viaAllTopics: boolean): void {
  topicViaAllTopics = viaAllTopics;
}

export function isTopicViaAllTopics(): boolean {
  return topicViaAllTopics;
}

// A followed feed opened from a tile (Home Discover, a Discover category page, Add Feed) also gets a
// back button to the preview return path (unfollowed feeds always have one, in their preview
// header). Main takes it over once and keeps it only while that feed stays selected.
let feedOpenedFromTile: string | undefined;

export function setFeedOpenedFromTile(feedId: string | undefined): void {
  feedOpenedFromTile = feedId;
}

export function getFeedOpenedFromTile(): string | undefined {
  return feedOpenedFromTile;
}

// A feed picked from the search suggestions (Home, Add Feed) opens like any feed: without a back
// button, even as the preview of an unfollowed feed. Main takes it over like feedOpenedFromTile.
let feedOpenedFromSearch: string | undefined;

export function setFeedOpenedFromSearch(feedId: string | undefined): void {
  feedOpenedFromSearch = feedId;
}

export function getFeedOpenedFromSearch(): string | undefined {
  return feedOpenedFromSearch;
}
